import React from 'react';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { AxiosError } from 'axios';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';
import { StyledTextField } from 'components/Input/styles';
import CustomSignatureField from 'containers/EntitiesFormSubmission/components/CustomSignatureField';
import { useStore } from 'utils/hooks/store';
import { SubmitAcknowledgementBody } from 'utils/api/modules/entities/entities.types';

import { Field, FieldLabel, InfoLabel, InfoRow, InfoValue, ModalContent } from './styles';

export type AcknowledgeConfirmModalProps = {
  documentName: string;
  prefilledName: string;
  onClose: () => void;
  onSubmit: (body: SubmitAcknowledgementBody) => Promise<void>;
};

type FormValues = {
  typedName: string;
  signature: string;
  accepted: boolean;
};

const validationSchema = Yup.object({
  typedName: Yup.string().trim().required('Full name is required'),
  signature: Yup.string().required(),
  accepted: Yup.boolean().oneOf([true]).required(),
});

const AcknowledgeConfirmModal = ({
  documentName,
  prefilledName,
  onClose,
  onSubmit,
}: AcknowledgeConfirmModalProps): React.ReactElement => {
  const { notification } = useStore();

  const handleFormSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>): Promise<void> => {
    try {
      await onSubmit({ typedName: values.typedName.trim(), signature: values.signature, accepted: true });
    } catch (error) {
      // Leave the modal open on failure so the recipient can retry.
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      notification.enqueueErrorSnackbar(detail || 'Something went wrong. Please try again.');
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={{ typedName: prefilledName, signature: '', accepted: false }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {({ values, errors, touched, isSubmitting, submitForm, setFieldValue, handleChange, handleBlur }) => {
        const canSubmit = Boolean(values.signature) && values.accepted && values.typedName.trim().length > 0;
        return (
          <CustomModal
            openModal
            onClose={onClose}
            modalTitle="Acknowledge document"
            cancelButton={
              <Button variant="outlined" size="large" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
            }
            submitButton={
              <Button variant="contained" size="large" onClick={submitForm} disabled={!canSubmit || isSubmitting}>
                Submit
              </Button>
            }
          >
            <ModalContent>
              <InfoRow>
                <InfoLabel>Document</InfoLabel>
                <InfoValue>{documentName}</InfoValue>
              </InfoRow>

              <Field>
                <FieldLabel htmlFor="typedName">Full name</FieldLabel>
                <StyledTextField
                  id="typedName"
                  name="typedName"
                  size="small"
                  fullWidth
                  value={values.typedName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.typedName && errors.typedName)}
                  helperText={touched.typedName ? errors.typedName : undefined}
                />
              </Field>

              <Field>
                <FieldLabel>Signature</FieldLabel>
                <CustomSignatureField
                  value={values.signature || undefined}
                  onChange={(dataUrl) => setFieldValue('signature', dataUrl)}
                  requiredNotFilled={Boolean(touched.signature && !values.signature)}
                />
              </Field>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.accepted}
                    onChange={(event) => setFieldValue('accepted', event.target.checked)}
                  />
                }
                label="I confirm I have read and understood this document."
              />
            </ModalContent>
          </CustomModal>
        );
      }}
    </Formik>
  );
};

export default AcknowledgeConfirmModal;
